import { useNavigate } from '@tanstack/react-router';
import { MoreHorizontal, PackageIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  BulkDeleteDialog,
  BulkSelectionToolbar,
  formatBulkCount,
  useBulkDelete,
} from '@/components/app/bulk-delete';
import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { ListPage } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOrgCurrency } from '@/hooks/use-org-currency';

import {
  type PackageWithItems,
  useDeletePackage,
  useListPackages,
} from '../api';

/** The services a package includes, in its order. */
function includesLabel(pkg: PackageWithItems): string {
  return [...pkg.items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) =>
      item.quantity > 1
        ? `${item.service.name} × ${item.quantity}`
        : item.service.name
    )
    .join(', ');
}

/** What buying it online would take up front, in a few words. */
function paymentLabel(
  pkg: PackageWithItems,
  format: (cents: number) => string
): string {
  switch (pkg.paymentPolicy) {
    case 'in_clinic':
      return 'Pay at clinic';
    case 'full':
      return 'Pay in full';
    case 'deposit':
      if (pkg.depositBasis === 'percent' && pkg.depositPercent != null)
        return `Deposit ${pkg.depositPercent}%`;
      if (pkg.depositAmountCents != null)
        return `Deposit ${format(pkg.depositAmountCents)}`;
      // No amount of its own: the clinic's default deposit applies.
      return 'Clinic deposit';
    default:
      return 'Clinic default';
  }
}

/**
 * Packages list, on the shared `ListPage` — the same shell as Memberships, so
 * the desktop table and the phone list render from one column config.
 */
export function PackagesPage() {
  const { packages, isLoading, isError, error } = useListPackages();
  const { deletePackage, isDeleting } = useDeletePackage();
  // Quiet instance for bulk delete, which raises one summary toast instead.
  const { deletePackageAsync: deletePackageQuietly } = useDeletePackage({
    showSuccessToast: false,
    showErrorToast: false,
  });
  const { format } = useOrgCurrency();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PackageWithItems | null>(
    null
  );

  // Create and edit live on the SHARED editor routes (`/create/package`,
  // `/edit/package/:id`) — see features/entity-editors.
  const openCreate = () =>
    void navigate({ params: { entity: 'package' }, to: '/create/$entity' });
  const openEdit = (pkg: PackageWithItems) =>
    void navigate({
      params: { entity: 'package', id: pkg.id },
      to: '/edit/$entity/$id',
    });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return packages;
    return packages.filter(
      (pkg) =>
        pkg.name.toLowerCase().includes(term) ||
        (pkg.description ?? '').toLowerCase().includes(term) ||
        pkg.items.some((item) => item.service.name.toLowerCase().includes(term))
    );
  }, [packages, search]);

  const rowIds = useMemo(() => rows.map((pkg) => pkg.id), [rows]);
  const bulk = useBulkDelete({
    deleteOne: deletePackageQuietly,
    noun: { singular: 'package', plural: 'packages' },
    rowIds,
    labelFor: (id) => packages.find((pkg) => pkg.id === id)?.name,
    resetKey: search,
  });

  const columns: ListColumn<PackageWithItems>[] = [
    {
      id: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: (pkg) => (
        <div>
          <div className="font-medium">{pkg.name}</div>
          <div
            className="hidden max-w-xs truncate text-muted-foreground text-xs md:block"
            title={includesLabel(pkg)}
          >
            {includesLabel(pkg)}
          </div>
        </div>
      ),
    },
    {
      id: 'price',
      header: 'Price',
      align: 'right',
      mobile: 'secondary',
      cell: (pkg) => (
        <span className="font-medium">{format(pkg.priceCents)}</span>
      ),
    },
    {
      id: 'validFor',
      header: 'Valid for',
      cell: (pkg) =>
        pkg.validityDays != null ? `${pkg.validityDays} days` : 'No expiry',
    },
    {
      id: 'payment',
      header: 'Payment',
      cell: (pkg) => paymentLabel(pkg, format),
    },
    {
      id: 'online',
      header: 'Online',
      cell: (pkg) => (pkg.sellOnline ? 'Sold online' : 'Not sold online'),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (pkg) => (
        <Badge variant={pkg.isActive ? 'default' : 'secondary'}>
          {pkg.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <title>Packages | Borrah</title>

      <ListPage<PackageWithItems>
        config={{
          title: 'Packages',
          columns,
          rows,
          rowKey: (pkg) => pkg.id,
          onRowClick: openEdit,
          rowActions: (pkg) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={`Actions for ${pkg.name}`}
                  size="icon"
                  variant="ghost"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEdit(pkg)}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteTarget(pkg)}
                  variant="destructive"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
          searchPlaceholder: 'Search packages',
          search,
          onSearchChange: setSearch,
          selection: bulk.selection,
          toolbar: bulk.hasSelection ? (
            <BulkSelectionToolbar bulk={bulk} />
          ) : undefined,
          primaryAction: {
            label: 'Add Package',
            mobileLabel: 'Add',
            onClick: openCreate,
          },
          isLoading,
          isError,
          errorMessage: error?.message ?? 'Failed to load packages',
          empty: {
            icon: PackageIcon,
            title: search ? 'No matching packages' : 'No packages yet',
            description: search
              ? 'No package matches that search.'
              : 'Bundle several services under one price.',
          },
        }}
      />

      <ConfirmDeleteDialog
        description="This removes the package from your catalogue. The services in it are not affected."
        isPending={isDeleting}
        onConfirm={() => {
          if (deleteTarget) deletePackage(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        open={!!deleteTarget}
        title={<>Delete &ldquo;{deleteTarget?.name}&rdquo;?</>}
      />

      <BulkDeleteDialog
        bulk={bulk}
        description={
          <>
            This removes {formatBulkCount(bulk.count, bulk.noun)} from your
            catalogue. The services in them are not affected.
          </>
        }
      />
    </>
  );
}
